import { useRef } from 'react'
import { motion, useInView } from 'framer-motion'
import { fadeInUp } from '../hooks/useScrollAnimation'

const experiences = [
  {
    id: 1,
    role: 'Senior Software Developer',
    company: 'BambooBox',
    color: '#00ff41',
    period: 'Jan 2026 → present',
    location: 'Bengaluru, India',
    type: 'Full-time',
    achievements: [
      'Led end-to-end development of an AI-powered B2B email campaign platform with campaign builder and real-time previews',
      'Architected Node.js microservice orchestrating GPT-4o via LangChain to generate hyper-personalized nurture emails at scale',
      'Built scalable backend email generation service using manager-worker job model (MongoDB + Redis) and LLM validation pipelines',
      'Integrated HubSpot, Mailchimp, and Pardot for campaign sync and analytics tracking',
      'Architected contact enrichment microservice with cursor-based MongoDB streaming and Redis caching to reduce LLM API calls',
    ],
    tech: ['Node.js', 'TypeScript', 'LangChain', 'GPT-4o', 'MongoDB', 'Redis', 'HubSpot'],
  },
  {
    id: 2,
    role: 'Software Developer',
    company: 'BambooBox',
    color: '#00d4ff',
    period: 'Jan 2024 → Dec 2025',
    location: 'Bengaluru, India',
    type: 'Full-time',
    achievements: [
      'Engineered real-time ad campaign synchronization system, reducing data sync latency with 99% uptime for Google and LinkedIn Ads',
      'Led architecture and development of scalable RESTful APIs, improving response time by 40%',
      'Optimized database queries, reducing average fetch time by 60% using MongoDB and PostgreSQL indexing',
      'Designed and launched an interactive account activity timeline for CRM users, boosting user engagement by 4x',
      'Conducted code reviews and mentored junior developers, reducing PR iteration cycles',
    ],
    tech: ['Node.js', 'PostgreSQL', 'MongoDB', 'Redis', 'Google Ads API', 'LinkedIn Ads API'],
  },
  {
    id: 3,
    role: 'Associate Software Developer',
    company: 'BambooBox',
    color: '#a78bfa',
    period: 'Feb 2023 → Dec 2023',
    location: 'Bengaluru, India',
    type: 'Full-time',
    achievements: [
      'Designed and implemented role-based access control (RBAC) to improve security and reduce unauthorized API requests',
      'Implemented job-worker scheduler to optimize task execution, ensuring 99.9% job execution reliability',
      'Contributed to CRM platform integrations including Salesforce and Zoho',
    ],
    tech: ['Node.js', 'Express.js', 'PostgreSQL', 'MongoDB', 'Salesforce', 'Zoho'],
  },
]

function TimelineCard({ exp, isLeft }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -60px 0px' })

  return (
    <div
      ref={ref}
      className={`relative hidden md:flex items-start gap-0 ${isLeft ? 'flex-row' : 'flex-row-reverse'}`}
    >
      {/* Card */}
      <motion.div
        initial={{ opacity: 0, x: isLeft ? -60 : 60 }}
        animate={isInView ? { opacity: 1, x: 0 } : {}}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="flex-1 max-w-[calc(50%-3rem)] glass-card border border-[#30363d] p-6 rounded-xl group hover:border-[#00ff41]/30 transition-all duration-300"
      >
        <div className="flex items-start justify-between mb-3 gap-3">
          <div>
            <h3 className="font-mono text-base font-bold text-[#e6edf3] group-hover:text-[#00ff41] transition-colors">
              {exp.role}
            </h3>
            <div className="flex items-center gap-2 mt-1">
              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: exp.color }} />
              <span className="font-mono text-sm font-semibold" style={{ color: exp.color }}>
                {exp.company}
              </span>
            </div>
          </div>
          <div className="text-right shrink-0">
            <span className="font-mono text-xs text-[#6e7681] bg-[#0d1117] px-2 py-1 rounded border border-[#30363d] block whitespace-nowrap">
              {exp.period}
            </span>
            <span className="font-mono text-xs text-[#6e7681] block mt-1">
              {exp.location} · {exp.type}
            </span>
          </div>
        </div>

        <ul className="space-y-2 mb-4">
          {exp.achievements.map((point, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-[#8b949e]">
              <span className="shrink-0 mt-0.5" style={{ color: exp.color }}>▸</span>
              <span>{point}</span>
            </li>
          ))}
        </ul>

        <div className="flex flex-wrap gap-1.5 pt-3 border-t border-[#30363d]">
          {exp.tech.map((t) => (
            <span
              key={t}
              className="font-mono text-xs px-2 py-0.5 rounded border"
              style={{
                color: exp.color,
                borderColor: `${exp.color}40`,
                backgroundColor: `${exp.color}08`,
              }}
            >
              {t}
            </span>
          ))}
        </div>
      </motion.div>

      {/* Center dot */}
      <div className="absolute left-1/2 -translate-x-1/2 top-7 z-10">
        <motion.div
          initial={{ scale: 0 }}
          animate={isInView ? { scale: 1 } : {}}
          transition={{ duration: 0.4, delay: 0.2 }}
          className="w-5 h-5 rounded-full border-2 border-[#0d1117] flex items-center justify-center"
          style={{ backgroundColor: exp.color, boxShadow: `0 0 12px ${exp.color}60` }}
        >
          <div className="w-2 h-2 rounded-full bg-[#0d1117]" />
        </motion.div>
      </div>

      {/* Spacer for opposite side */}
      <div className="flex-1 max-w-[calc(50%-3rem)]" />
    </div>
  )
}

function MobileCard({ exp }) {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -60px 0px' })

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 30 }}
      animate={isInView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6 }}
      className="relative"
    >
      {/* Dot */}
      <div
        className="absolute -left-[2.15rem] top-5 w-4 h-4 rounded-full border-2 border-[#0d1117]"
        style={{ backgroundColor: exp.color, boxShadow: `0 0 10px ${exp.color}60` }}
      />
      {/* Card */}
      <div className="glass-card border border-[#30363d] p-5 rounded-xl">
        <h3 className="font-mono text-sm font-bold text-[#e6edf3] mb-1">{exp.role}</h3>
        <span className="font-mono text-sm" style={{ color: exp.color }}>{exp.company}</span>
        <p className="font-mono text-xs text-[#6e7681] mt-1 mb-3">
          {exp.period} · {exp.location}
        </p>
        <ul className="space-y-2 mb-3">
          {exp.achievements.slice(0, 3).map((point, j) => (
            <li key={j} className="flex items-start gap-2 text-xs text-[#8b949e]">
              <span className="shrink-0 mt-0.5" style={{ color: exp.color }}>▸</span>
              {point}
            </li>
          ))}
        </ul>
        <div className="flex flex-wrap gap-1">
          {exp.tech.slice(0, 5).map((t) => (
            <span
              key={t}
              className="font-mono text-xs px-2 py-0.5 rounded border"
              style={{ color: exp.color, borderColor: `${exp.color}40`, backgroundColor: `${exp.color}08` }}
            >
              {t}
            </span>
          ))}
        </div>
      </div>
    </motion.div>
  )
}

export default function Experience() {
  const ref = useRef(null)
  const isInView = useInView(ref, { once: true, margin: '0px 0px -80px 0px' })

  return (
    <section id="experience" className="py-24 relative">
      <div className="max-w-7xl mx-auto px-6 lg:px-8" ref={ref}>
        {/* Header */}
        <motion.div
          variants={fadeInUp}
          initial="hidden"
          animate={isInView ? 'visible' : 'hidden'}
          className="mb-16"
        >
          <p className="font-mono text-[#00ff41] text-sm mb-2">// 03. experience</p>
          <h2 className="section-title">
            Work <span>History</span>
          </h2>
          <div className="h-0.5 w-24 bg-gradient-to-r from-[#00ff41] to-transparent mt-3" />
        </motion.div>

        {/* Desktop Timeline */}
        <div className="hidden md:block relative">
          {/* Center vertical line */}
          <div className="absolute left-1/2 top-0 bottom-0 w-px -translate-x-1/2">
            <div className="w-full h-full bg-gradient-to-b from-[#00ff41]/60 via-[#00d4ff]/40 to-transparent" />
          </div>

          <div className="space-y-12">
            {experiences.map((exp, i) => (
              <TimelineCard key={exp.id} exp={exp} isLeft={i % 2 === 0} />
            ))}
          </div>
        </div>

        {/* Mobile Timeline */}
        <div className="md:hidden relative">
          <div className="absolute left-5 top-0 bottom-0 w-px bg-gradient-to-b from-[#00ff41]/60 to-transparent" />
          <div className="space-y-8 pl-10">
            {experiences.map((exp) => (
              <MobileCard key={exp.id} exp={exp} />
            ))}
          </div>
        </div>
      </div>
    </section>
  )
}
